import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { Customer } from '../Models/customer.model';
import {
  Auftrag,
  AuftragAssignment,
  AuftragAssignmentCreate,
  AuftragCreate,
  AuftragOverview,
  CustomerRef,
  EinsatzMaterial,
  PersonHours,
  PlannableUser,
  SpontanEinsatzCreate,
  StammkundeMember,
  StammkundeWorker,
  WarehouseRef,
  ZeitEintrag,
  ZeitEintragCreate
} from '../Models/stammkunde.model';

/**
 * API-Zugriff für den Stammkunden-Bereich: Aufträge, Einsatzplanung je Auftrag,
 * Team-Mitgliedschaften und Lager-Verknüpfung.
 */
@Injectable({ providedIn: 'root' })
export class StammkundeService {
  private customerUrl = environment.apiBaseUrl + '/customer';
  private auftragUrl = environment.apiBaseUrl + '/auftrag';
  private accessUrl = environment.apiBaseUrl + '/access';

  constructor(private http: HttpClient) { }

  // ── Stammkunden (Kunden mit Type=Stammkunde) ──

  getStammkunden(): Observable<Customer[]> {
    return this.http.get<Customer[]>(`${this.customerUrl}?type=Stammkunde`);
  }

  // ── Aufträge ──

  getAuftraege(customerId?: number): Observable<Auftrag[]> {
    const query = customerId != null ? `?customerId=${customerId}` : '';
    return this.http.get<Auftrag[]>(`${this.auftragUrl}${query}`);
  }

  getAuftrag(id: number): Observable<Auftrag> {
    return this.http.get<Auftrag>(`${this.auftragUrl}/${id}`);
  }

  createAuftrag(auftrag: AuftragCreate): Observable<Auftrag> {
    return this.http.post<Auftrag>(this.auftragUrl, auftrag);
  }

  updateAuftrag(id: number, auftrag: AuftragCreate): Observable<Auftrag> {
    return this.http.put<Auftrag>(`${this.auftragUrl}/${id}`, auftrag);
  }

  deleteAuftrag(id: number): Observable<void> {
    return this.http.delete<void>(`${this.auftragUrl}/${id}`);
  }

  // ── Einsatzplanung je Auftrag ──

  getAssignments(auftragId: number): Observable<AuftragAssignment[]> {
    return this.http.get<AuftragAssignment[]>(`${this.auftragUrl}/${auftragId}/assignments`);
  }

  createAssignment(auftragId: number, assignment: AuftragAssignmentCreate): Observable<AuftragAssignment> {
    return this.http.post<AuftragAssignment>(`${this.auftragUrl}/${auftragId}/assignments`, assignment);
  }

  updateAssignment(auftragId: number, assignmentId: number, assignment: AuftragAssignmentCreate): Observable<AuftragAssignment> {
    return this.http.put<AuftragAssignment>(`${this.auftragUrl}/${auftragId}/assignments/${assignmentId}`, assignment);
  }

  deleteAssignment(auftragId: number, assignmentId: number, sendCancellation = false): Observable<void> {
    return this.http.delete<void>(
      `${this.auftragUrl}/${auftragId}/assignments/${assignmentId}?sendCancellation=${sendCancellation}`);
  }

  getPlannableUsers(): Observable<PlannableUser[]> {
    return this.http.get<PlannableUser[]>(`${this.auftragUrl}/plannable-users`);
  }

  /** Alle Einsätze eines Stammkunden über alle Aufträge (für das Planungsboard). */
  getCustomerAssignments(customerId: number, from: string, to: string): Observable<AuftragAssignment[]> {
    return this.http.get<AuftragAssignment[]>(
      `${this.auftragUrl}/customer/${customerId}/assignments?from=${from}&to=${to}`);
  }

  // ── Meine Einsätze (Vorarbeiter-Sicht) ──

  /** Eigene Einsätze über alle Stammkunden-Aufträge, optional auf einen Zeitraum. */
  getMyAssignments(from?: string, to?: string): Observable<AuftragAssignment[]> {
    const params: string[] = [];
    if (from) { params.push(`from=${from}`); }
    if (to) { params.push(`to=${to}`); }
    const query = params.length ? `?${params.join('&')}` : '';
    return this.http.get<AuftragAssignment[]>(`${this.auftragUrl}/my-assignments${query}`);
  }

  /** Arbeitsstatus eines Einsatzes setzen (Bestätigt/Unterwegs/Erledigt). */
  setAssignmentWorkStatus(auftragId: number, assignmentId: number, status: string): Observable<AuftragAssignment> {
    return this.http.post<AuftragAssignment>(
      `${this.auftragUrl}/${auftragId}/assignments/${assignmentId}/work-status`, { status });
  }

  /** Stammkunden, für die der Benutzer einen Spontan-Einsatz anlegen darf. */
  getMyCustomers(): Observable<CustomerRef[]> {
    return this.http.get<CustomerRef[]>(`${this.auftragUrl}/my-customers`);
  }

  /** Spontan-Einsatz anlegen (Vorarbeiter teilt sich selbst ein). */
  createSpontanEinsatz(einsatz: SpontanEinsatzCreate): Observable<AuftragAssignment> {
    return this.http.post<AuftragAssignment>(`${this.auftragUrl}/spontan`, einsatz);
  }

  /** Material eines Einsatzes vollständig ersetzen (Vorarbeiter dokumentiert Verbrauch). */
  setAssignmentMaterials(auftragId: number, assignmentId: number, materials: EinsatzMaterial[]): Observable<AuftragAssignment> {
    return this.http.put<AuftragAssignment>(
      `${this.auftragUrl}/${auftragId}/assignments/${assignmentId}/materials`, materials);
  }

  // ── Stunden-Erfassung (ohne Preise) ──

  getTimeEntries(auftragId: number): Observable<ZeitEintrag[]> {
    return this.http.get<ZeitEintrag[]>(`${this.auftragUrl}/${auftragId}/time-entries`);
  }

  addTimeEntry(auftragId: number, entry: ZeitEintragCreate): Observable<ZeitEintrag> {
    return this.http.post<ZeitEintrag>(`${this.auftragUrl}/${auftragId}/time-entries`, entry);
  }

  updateTimeEntry(auftragId: number, entryId: number, entry: ZeitEintragCreate): Observable<ZeitEintrag> {
    return this.http.put<ZeitEintrag>(`${this.auftragUrl}/${auftragId}/time-entries/${entryId}`, entry);
  }

  deleteTimeEntry(auftragId: number, entryId: number): Observable<void> {
    return this.http.delete<void>(`${this.auftragUrl}/${auftragId}/time-entries/${entryId}`);
  }

  approveTimeEntry(auftragId: number, entryId: number): Observable<ZeitEintrag> {
    return this.http.post<ZeitEintrag>(`${this.auftragUrl}/${auftragId}/time-entries/${entryId}/approve`, {});
  }

  reopenTimeEntry(auftragId: number, entryId: number): Observable<ZeitEintrag> {
    return this.http.post<ZeitEintrag>(`${this.auftragUrl}/${auftragId}/time-entries/${entryId}/reopen`, {});
  }

  /** Alle offenen Stunden eines Auftrags auf einmal freigeben. */
  approveAllTimeEntries(auftragId: number): Observable<{ approved: number }> {
    return this.http.post<{ approved: number }>(`${this.auftragUrl}/${auftragId}/time-entries/approve-all`, {});
  }

  // ── Material-Freigabe ──

  approveMaterial(auftragId: number, materialId: number): Observable<EinsatzMaterial> {
    return this.http.post<EinsatzMaterial>(`${this.auftragUrl}/${auftragId}/materials/${materialId}/approve`, {});
  }

  reopenMaterial(auftragId: number, materialId: number): Observable<EinsatzMaterial> {
    return this.http.post<EinsatzMaterial>(`${this.auftragUrl}/${auftragId}/materials/${materialId}/reopen`, {});
  }

  /** Alle offenen Materialposten eines Auftrags auf einmal freigeben. */
  approveAllMaterials(auftragId: number): Observable<{ approved: number }> {
    return this.http.post<{ approved: number }>(`${this.auftragUrl}/${auftragId}/materials/approve-all`, {});
  }

  // ── Bauleiter-Gesamtübersicht ──

  getOverview(customerId?: number, from?: string, to?: string): Observable<AuftragOverview[]> {
    const params: string[] = [];
    if (customerId != null) { params.push(`customerId=${customerId}`); }
    if (from) { params.push(`from=${from}`); }
    if (to) { params.push(`to=${to}`); }
    const query = params.length ? `?${params.join('&')}` : '';
    return this.http.get<AuftragOverview[]>(`${this.auftragUrl}/overview${query}`);
  }

  getHoursByPerson(customerId?: number, from?: string, to?: string): Observable<PersonHours[]> {
    const params: string[] = [];
    if (customerId != null) { params.push(`customerId=${customerId}`); }
    if (from) { params.push(`from=${from}`); }
    if (to) { params.push(`to=${to}`); }
    const query = params.length ? `?${params.join('&')}` : '';
    return this.http.get<PersonHours[]>(`${this.auftragUrl}/overview/by-person${query}`);
  }

  /** Person per Drag & Drop einem Einsatz zuteilen. */
  addParticipant(auftragId: number, assignmentId: number, userId: string): Observable<AuftragAssignment> {
    return this.http.post<AuftragAssignment>(
      `${this.auftragUrl}/${auftragId}/assignments/${assignmentId}/participants/${userId}`, {});
  }

  /** Person (Teilnehmer) von einem Einsatz entfernen. */
  removeParticipant(auftragId: number, assignmentId: number, userId: string): Observable<AuftragAssignment> {
    return this.http.delete<AuftragAssignment>(
      `${this.auftragUrl}/${auftragId}/assignments/${assignmentId}/participants/${userId}`);
  }

  /** Arbeiter (ohne Login) per Drag & Drop einem Einsatz zuteilen. */
  addWorkerToAssignment(auftragId: number, assignmentId: number, workerId: number): Observable<AuftragAssignment> {
    return this.http.post<AuftragAssignment>(
      `${this.auftragUrl}/${auftragId}/assignments/${assignmentId}/workers/${workerId}`, {});
  }

  /** Arbeiter (ohne Login) von einem Einsatz entfernen. */
  removeWorkerFromAssignment(auftragId: number, assignmentId: number, workerId: number): Observable<AuftragAssignment> {
    return this.http.delete<AuftragAssignment>(
      `${this.auftragUrl}/${auftragId}/assignments/${assignmentId}/workers/${workerId}`);
  }

  // ── Arbeiter (ohne Login) im Team ──

  getWorkers(customerId: number): Observable<StammkundeWorker[]> {
    return this.http.get<StammkundeWorker[]>(`${environment.apiBaseUrl}/stammkunde/${customerId}/workers`);
  }

  addWorker(customerId: number, worker: { name: string; phone?: string }): Observable<StammkundeWorker> {
    return this.http.post<StammkundeWorker>(`${environment.apiBaseUrl}/stammkunde/${customerId}/workers`, worker);
  }

  updateWorker(customerId: number, workerId: number, worker: { name: string; phone?: string }): Observable<StammkundeWorker> {
    return this.http.put<StammkundeWorker>(
      `${environment.apiBaseUrl}/stammkunde/${customerId}/workers/${workerId}`, worker);
  }

  deleteWorker(customerId: number, workerId: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiBaseUrl}/stammkunde/${customerId}/workers/${workerId}`);
  }

  // ── Team (Mitgliedschaften) ──

  getMembers(customerId: number): Observable<StammkundeMember[]> {
    return this.http.get<StammkundeMember[]>(`${this.accessUrl}/stammkunde/${customerId}/members`);
  }

  setMembers(customerId: number, members: StammkundeMember[]): Observable<void> {
    return this.http.put<void>(`${this.accessUrl}/stammkunde/${customerId}/members`, members);
  }

  // ── Lager-Verknüpfung ──

  getLinkedWarehouses(customerId: number): Observable<WarehouseRef[]> {
    return this.http.get<WarehouseRef[]>(`${this.accessUrl}/stammkunde/${customerId}/warehouses`);
  }

  linkWarehouse(customerId: number, warehouseId: number): Observable<void> {
    return this.http.post<void>(`${this.accessUrl}/stammkunde/${customerId}/warehouses/${warehouseId}`, {});
  }

  unlinkWarehouse(customerId: number, warehouseId: number): Observable<void> {
    return this.http.delete<void>(`${this.accessUrl}/stammkunde/${customerId}/warehouses/${warehouseId}`);
  }

  getMyWarehouses(): Observable<WarehouseRef[]> {
    return this.http.get<WarehouseRef[]>(`${this.accessUrl}/my-warehouses`);
  }
}
